import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Header from './Header';
import Charts from './charts/Charts';
import './Dashboard.css';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  total_students: number;
  total_classes: number;
  enrolled_students: number;
  unassigned_students: number;
  recent_students_30_days: number;
  has_students: boolean;
  has_classes: boolean;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading, isTeacher, institutions, organization, user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If a teacher account isn't initialized (no institution/org), show a setup screen instead of calling tenant-scoped APIs.
    const needsSetup = !!isTeacher && (institutions.length === 0 || !organization);

    if (!authLoading && isAuthenticated && !needsSetup) {
      fetchDashboardStats();
    } else if (!authLoading && isAuthenticated && needsSetup) {
      setLoading(false);
      setError(null);
      setStats(null);
    }
  }, [authLoading, isAuthenticated, isTeacher, institutions.length, organization]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      if (!token) {
        throw new Error(t('auth.noTokenAvailable'));
      }

      const response = await fetch('/api/dashboard/stats', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch stats: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
        setError(null);
      } else {
        throw new Error(data.error || t('dashboard.errors.fetchStatsFailed'));
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err instanceof Error ? err.message : t('common.unknownError'));
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const onClickableCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, onActivate: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };

  const handleClassesClick = () => {
    navigate('/classes');
  };

  const handleStudentsClick = () => {
    navigate('/students');
  };

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <div className="welcome-section">
            {!!isTeacher && (institutions.length === 0 || !organization) ? (
              <>
                <h1>{t('onboarding.setupRequired.title')}</h1>
                <p>{t('onboarding.setupRequired.subtitle')}</p>
                <div
                  className="error-message"
                  style={{
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    padding: '12px',
                    borderRadius: '6px',
                    marginTop: '20px',
                    border: '1px solid #fde68a'
                  }}
                >
                  <p>{t('onboarding.setupRequired.body')}</p>
                  <p>
                    <strong>{t('onboarding.setupRequired.yourEmailLabel')}</strong> {user?.email || t('common.unknown')}
                  </p>
                  <p style={{ marginBottom: 0 }}>{t('onboarding.setupRequired.afterAssigned')}</p>
                </div>
              </>
            ) : (
              <>
                <h1>{t('dashboard.welcome')}</h1>
                <p>{t('dashboard.subtitle')}</p>
              </>
            )}
            
            {!!isTeacher && (institutions.length === 0 || !organization) ? null : (
              <div className="dashboard-cards">
              <div
                className="dashboard-card clickable"
                onClick={handleClassesClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => onClickableCardKeyDown(e, handleClassesClick)}
                aria-label={t('dashboard.cards.classes.aria')}
              >
                <div className="card-icon classrooms-icon">
                  <SchoolIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                </div>
                <h3>{t('sidebar.classes')}</h3>
                <p>{t('dashboard.cards.classes.description')}</p>
                <span className="card-count">
                  {authLoading || loading ? t('common.loading') :
                   !isAuthenticated ? t('auth.notAuthenticated') :
                   error ? t('common.error') :
                   stats ? t('dashboard.stats.classes', { count: stats.total_classes }) : t('dashboard.stats.classes', { count: 0 })}
                </span>
              </div>
              
              <div
                className="dashboard-card clickable"
                onClick={handleStudentsClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => onClickableCardKeyDown(e, handleStudentsClick)}
                aria-label={t('dashboard.cards.students.aria')}
              >
                <div className="card-icon students-icon">
                  <GroupIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                </div>
                <h3>{t('sidebar.students')}</h3>
                <p>{t('dashboard.cards.students.description')}</p>
                <span className="card-count">
                  {authLoading || loading ? t('common.loading') :
                   !isAuthenticated ? t('auth.notAuthenticated') :
                   error ? t('common.error') :
                   stats ? t('dashboard.stats.students', { count: stats.total_students }) : t('dashboard.stats.students', { count: 0 })}
                </span>
                {stats && stats.unassigned_students > 0 && (
                  <div className="card-detail">
                    <small>{t('dashboard.stats.unassigned', { count: stats.unassigned_students })}</small>
                  </div>
                )}
              </div>
              
              <div className="dashboard-card">
                <div className="card-icon google-icon">
                  <MenuBookIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                </div>
                <h3>{t('dashboard.recentActivity')}</h3>
                <p>{t('dashboard.recentActivityDescription')}</p>
                <span className="card-count">
                  {authLoading || loading ? t('common.loading') :
                   !isAuthenticated ? t('auth.notAuthenticated') :
                   error ? t('common.error') :
                   stats ? t('dashboard.stats.recent', { count: stats.recent_students_30_days }) : t('dashboard.stats.recent', { count: 0 })}
                </span>
              </div>
              </div>
            )}

            {error && isAuthenticated && !(!!isTeacher && (institutions.length === 0 || !organization)) && (
              <div className="error-message" style={{ 
                backgroundColor: '#fee2e2', 
                color: '#dc2626', 
                padding: '12px', 
                borderRadius: '6px', 
                marginTop: '20px',
                border: '1px solid #fecaca'
              }}>
                <p>{t('dashboard.errors.loadStatsFailedPrefix', { error })}</p>
                <button 
                  onClick={fetchDashboardStats}
                  aria-label={t('dashboard.actions.retryLoadStatsAria')}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                >
                  {t('common.retry')}
                </button>
              </div>
            )}

            {!isAuthenticated && !authLoading && (
              <div className="error-message" style={{ 
                backgroundColor: '#fef3c7', 
                color: '#92400e', 
                padding: '12px', 
                borderRadius: '6px', 
                marginTop: '20px',
                border: '1px solid #fde68a'
              }}>
                <p>{t('dashboard.errors.loginToViewStats')}</p>
              </div>
            )}

            {/* Charts Section - only show if authenticated and has data */}
            {!authLoading && isAuthenticated && !error && stats && (stats.has_students || stats.has_classes) && !(!!isTeacher && (institutions.length === 0 || !organization)) && (
              <div className="charts-section">
                <Charts />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
